import { module as BaseModule } from "modujs";

const SELECTORS = {
  wrapper: "[data-module-slideshow-wrapper]",
  images: "[data-slideshow-image]",
  bullets: "[data-slideshow-bullet]",
  previous: "[data-slideshow-previous]",
  next: "[data-slideshow-next]",
};

export default class Slideshow extends BaseModule {
  init() {
    this.wrapper = this.el.querySelector(SELECTORS.wrapper);
    this.images = Array.from(this.el.querySelectorAll(SELECTORS.images));
    this.bullets = Array.from(this.el.querySelectorAll(SELECTORS.bullets));
    this.previousButton = this.el.querySelector(SELECTORS.previous);
    this.nextButton = this.el.querySelector(SELECTORS.next);
    this.currentIndex = this.getInitialIndex();

    if (!this.images.length) {
      return;
    }

    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.attachButtonListeners();
    this.attachBulletListeners();
    this.observeSlideSizeChanges();
    this.updateSlides();
  }

  getInitialIndex() {
    const selectedIndex = this.images.findIndex((image) =>
      image.classList.contains("is-selected")
    );

    return selectedIndex === -1 ? 0 : selectedIndex;
  }

  attachButtonListeners() {
    if (this.previousButton) {
      this.previousButton.addEventListener("click", this.handlePrevious);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.handleNext);
    }
  }

  attachBulletListeners() {
    this.bullets.forEach((bullet, index) => {
      const handler = (event) => {
        event.preventDefault();
        this.goToSlide(index);
      };

      bullet.addEventListener("click", handler);
      bullet._slideshowHandler = handler;
    });
  }

  observeSlideSizeChanges() {
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", this.handleResize);
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateContainerHeight();
    });

    this.images.forEach((image) => this.resizeObserver.observe(image));
  }

  handlePrevious(event) {
    event.preventDefault();
    this.goToSlide(this.currentIndex - 1);
  }

  handleNext(event) {
    event.preventDefault();
    this.goToSlide(this.currentIndex + 1);
  }

  handleResize() {
    this.updateContainerHeight();
  }

  goToSlide(index) {
    if (!this.images.length) {
      return;
    }

    const boundedIndex = Math.max(0, Math.min(this.images.length - 1, index));

    if (boundedIndex === this.currentIndex) {
      return;
    }

    this.currentIndex = boundedIndex;
    this.updateSlides();
  }

  updateSlides() {
    this.images.forEach((image, index) => {
      image.classList.toggle("is-selected", index === this.currentIndex);
    });

    this.bullets.forEach((bullet, index) => {
      bullet.classList.toggle("is-selected", index === this.currentIndex);
    });

    this.updateControls();
    this.pauseInactiveVideos();
    this.updateContainerHeight();
  }

  pauseInactiveVideos() {
    this.images.forEach((image, index) => {
      if (index === this.currentIndex) {
        return;
      }

      const iframes = Array.from(image.querySelectorAll("iframe"));

      iframes.forEach((iframe) => {
        if (!iframe?.contentWindow) {
          return;
        }

        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: [],
            }),
            "*"
          );
        } catch (error) {
          // Ignore cross-origin player messaging failures.
        }
      });
    });
  }

  updateControls() {
    const atStart = this.currentIndex === 0;
    const atEnd = this.currentIndex === this.images.length - 1;

    if (this.previousButton) {
      this.previousButton.disabled = atStart;
      this.previousButton.setAttribute("aria-disabled", String(atStart));
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.setAttribute("aria-disabled", String(atEnd));
    }
  }

  updateContainerHeight() {
    const activeImage = this.images[this.currentIndex];
    const target = this.wrapper || this.el;

    if (!activeImage || !target) {
      return;
    }

    const newHeight = activeImage.offsetHeight;

    if (newHeight) {
      target.style.height = `${newHeight}px`;
    }
  }

  detachButtonListeners() {
    if (this.previousButton) {
      this.previousButton.removeEventListener("click", this.handlePrevious);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.handleNext);
    }
  }

  detachBulletListeners() {
    this.bullets.forEach((bullet) => {
      if (bullet._slideshowHandler) {
        bullet.removeEventListener("click", bullet._slideshowHandler);
        delete bullet._slideshowHandler;
      }
    });
  }

  destroy() {
    this.detachButtonListeners();
    this.detachBulletListeners();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener("resize", this.handleResize);
    }

    if (this.wrapper) {
      this.wrapper.style.height = "";
    } else {
      this.el.style.height = "";
    }

    this.images = [];
    this.bullets = [];
    this.wrapper = null;
  }
}
